import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, EyeOff, Lock, Trash2, Mail } from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../auth/AuthContext";

/**
 * Plain-English Privacy Promise — short, scannable page that anyone can read in 60 seconds.
 * Linked from footer, signup, checkout, chat. Separate from the formal /privacy policy
 * (which is the legal long-form). This page is the *promise*; /privacy is the *policy*.
 */
export default function PrivacyPromisePage() {
  const { user } = useAuth();
  useEffect(() => {
    document.title = "Privacy Promise — Ascend Higher";
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="px-6 py-6 max-w-5xl mx-auto">
        <Link
          to={user ? "/dashboard" : "/"}
          className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
        >
          <ArrowLeft size={16} /> {user ? "Dashboard" : "Back"}
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-20">
        <p className="font-mono-eyebrow text-[#3e6b52] mb-3">Our Privacy Promise</p>
        <h1 className="font-display text-[36px] sm:text-[48px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
          Your conversations stay{" "}
          <span className="italic font-light text-[#3e6b52]">yours</span>.
        </h1>
        <p className="font-body text-[16px] text-[#4b5563] mt-5 leading-relaxed">
          Ascend is a place to be honest — about money pressure, your goals, your relationships, your fears, your wins. We treat that like a vault. Only you hold the key.
        </p>

        <div className="mt-10 rounded-2xl bg-white border border-[#eadfc9] p-6 sm:p-8 space-y-5">
          {[
            {
              icon: ShieldCheck,
              title: "We do not sell your data.",
              body: "Not to advertisers. Not to data brokers. Not to anyone, ever.",
            },
            {
              icon: EyeOff,
              title: "We do not share your private entries.",
              body: "Your messages with your coach, your goals, your reflections — they are not used to train public AI models, not shown to other users, and not posted publicly anywhere by us.",
            },
            {
              icon: Lock,
              title: "Your data is encrypted in transit.",
              body: "TLS 1.2+. Stored on hardened cloud infrastructure with restricted, audited access. Only the engineering staff that maintains the system can ever access stored data, and only when needed for security, debugging, or your direct support request.",
            },
            {
              icon: Trash2,
              title: "You can delete everything, anytime.",
              body: "One click in your account. We delete personal data within 30 days, except records we are legally required to retain (e.g. tax/billing receipts).",
            },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#e9f0ec] flex items-center justify-center shrink-0">
                <Icon size={18} className="text-[#3e6b52]" />
              </div>
              <div>
                <p className="font-display text-[20px] text-[#0f1724] leading-tight">{title}</p>
                <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mt-1">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-[#faf6ec] border border-[#eadfc9] p-6">
          <p className="font-mono-eyebrow text-[#486a8f] mb-2">When we'd ever hand over data</p>
          <p className="font-body text-[14px] text-[#2b3442] leading-relaxed">
            Only if{" "}
            <span className="font-semibold text-[#0f1724]">you specifically ask us to</span>, or if we are{" "}
            <span className="font-semibold text-[#0f1724]">legally compelled</span> by a valid court order, subpoena, or credible imminent-harm safety report (for example, a credible disclosure of imminent risk to life). Even then, we provide only what is legally required and notify you when permitted.
          </p>
        </div>

        <div className="mt-8 rounded-2xl bg-white border border-[#eadfc9] p-6">
          <p className="font-mono-eyebrow text-[#486a8f] mb-2">A note on what this is</p>
          <p className="font-body text-[14px] text-[#4b5563] leading-relaxed">
            Ascend Higher is a personal growth and life-organization tool. It is{" "}
            <span className="font-medium text-[#0f1724]">not therapy, medical care, legal advice, financial advice, or emergency support</span>. If you need formally privileged communication for legal, medical, or therapeutic reasons, please consult a licensed professional. If you are in crisis, please call or text{" "}
            <a href="tel:988" className="text-[#486a8f] font-medium hover:underline">988</a> (US).
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link
            to="/privacy"
            data-testid="privacy-promise-full-policy-link"
            className="flex-1 text-center px-5 py-3 rounded-full bg-[#0f1724] text-white font-body text-[14px] font-medium hover:bg-[#1c2944] transition-colors"
          >
            Read the full Privacy Policy
          </Link>
          <a
            href="mailto:support@ascendhigher.life"
            data-testid="privacy-promise-contact-link"
            className="flex-1 text-center px-5 py-3 rounded-full bg-white border border-[#eadfc9] text-[#2b3442] font-body text-[14px] font-medium hover:bg-[#faf6ec] transition-colors inline-flex items-center justify-center gap-2"
          >
            <Mail size={14} /> Email a privacy question
          </a>
        </div>

        <p className="font-body text-[12px] text-[#9aa3ae] text-center mt-8">
          Last updated February 4, 2026 · Ascendapp LLC
        </p>
      </main>

      <Footer />
    </div>
  );
}
